/**
 * WorkOS auth — single source of truth for API auth.
 *
 * Token source: (1) Optional getter from AuthKit (setAccessTokenGetter); (2) sessionStorage
 * (SESSION_TOKEN_KEY) from backend redirect fragment (app/#session=TOKEN). In this app the backend
 * handles login/callback and redirects with the fragment, so the getter is often unset and we rely
 * on fragment handoff. Production: WorkOS only. Dev: optional legacy localStorage fallback when
 * WorkOS is not configured.
 */

let accessTokenGetter: (() => Promise<string | null>) | null = null;
let workOSAuthenticated = false;

const _workosClientId = (typeof import.meta !== 'undefined' && import.meta.env
  ? (import.meta.env.VITE_WORKOS_CLIENT_ID as string | undefined)
  : '') ?? '';
if (!import.meta.env.DEV && (!_workosClientId || !_workosClientId.trim())) {
  console.error(
    'VITE_WORKOS_CLIENT_ID is not set — authentication will not work in production. Add this to your Vercel environment variables.'
  );
}

/**
 * Key for session token passed via redirect fragment (when cookie is blocked).
 * Backend redirects to app/#session=TOKEN; we store it here and send as Bearer.
 */
export const SESSION_TOKEN_KEY = 'workos_session_token';

export function isWorkOSEnabled(): boolean {
  const id = _workosClientId;
  return Boolean(id && id.trim() !== '');
}

/**
 * Register the function that returns the current WorkOS access token (e.g. from AuthKit React).
 * Optional: when unset, getAccessToken() falls back to sessionStorage (fragment handoff).
 */
export function setAccessTokenGetter(getter: () => Promise<string | null>): void {
  accessTokenGetter = getter;
}

/** Set WorkOS session state so isAuthenticated() can reflect it without hooks. */
export function setWorkOSAuthenticated(authenticated: boolean): void {
  workOSAuthenticated = authenticated;
}

/** Whether the user is authenticated via WorkOS (set by UserContext). */
export function getWorkOSAuthenticated(): boolean {
  return workOSAuthenticated;
}

/** Clear session token from fragment handoff (call on logout). */
export function clearSessionToken(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
  }
}

/**
 * Returns the current access token. When WorkOS is enabled: tries the registered getter first (if
 * set), then sessionStorage (fragment handoff). In this app the getter is often unset, so the token
 * typically comes from sessionStorage after the backend redirects to app/#session=TOKEN.
 */
export async function getAccessToken(): Promise<string | null> {
  if (!isWorkOSEnabled()) {
    return null;
  }
  if (accessTokenGetter) {
    try {
      const t = await accessTokenGetter();
      if (t) return t;
    } catch {
      // fall through to session token
    }
  }
  if (typeof sessionStorage !== 'undefined') {
    const stored = sessionStorage.getItem(SESSION_TOKEN_KEY);
    if (stored) return stored;
  }
  return null;
}

/**
 * Returns auth headers for fetch/axios. WorkOS-only in production; dev-only legacy fallback when WorkOS is disabled.
 * In production we always send fragment-handoff token from sessionStorage first (cookie often not sent cross-origin).
 */
export async function getAuthHeaders(): Promise<HeadersInit> {
  // Production: always try sessionStorage first so Bearer is sent even when cookie is blocked
  if (typeof import.meta !== 'undefined' && import.meta.env.PROD && typeof sessionStorage !== 'undefined') {
    const stored = sessionStorage.getItem(SESSION_TOKEN_KEY);
    if (stored) return { Authorization: `Bearer ${stored}` };
  }
  if (isWorkOSEnabled()) {
    const token = await getAccessToken();
    if (token) return { Authorization: `Bearer ${token}` };
    return {};
  }
  // Dev only: legacy token when WorkOS is not configured
  const token =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('token') || sessionStorage.getItem('token')
      : null;
  if (token) return { Authorization: `Bearer ${token}` };
  return {};
}
